// Label canvas document — the persisted `label_mockups.canvas_data` shape.
//
// ALL geometry is in LABEL MILLIMETRES (origin top-left of the front label,
// x to the right, y down) so a design re-renders identically on screen, in the
// downloaded PNG and in a future print hand-off at any DPI (M4 plan decision 1).
// Pure TypeScript: no React, no browser globals — safe to import during SSR
// and from a plain Node script.

import { FONT_KEYS, type FontKey } from "./fonts";
import { isOrnamentKey, type OrnamentKey } from "./ornaments";

/**
 * Mirrors LabelMockup::CANVAS_VERSION. Bump + extend migrate() on shape changes.
 * v1 (M4): text, image, rect, ellipse, line.
 * v2 (M4 fix-up W5): adds the ornament element and the image slot's optional
 *     `placeholder` caption. A v1 document is a valid v2 document once
 *     re-stamped, so migrate() upgrades it in place.
 */
export const CANVAS_VERSION = 2 as const;

/** Every version migrate() can read (older first). */
export const READABLE_CANVAS_VERSIONS: readonly number[] = [1, 2] as const;

export type BottleType = "wine" | "mcc";
export const BOTTLE_TYPES: readonly BottleType[] = ["wine", "mcc"] as const;

/** Mirrors LabelMockup::LABEL_MM exactly (portrait front labels, mm). */
export const LABEL_MM: Readonly<Record<BottleType, { width: number; height: number }>> = {
  wine: { width: 90, height: 100 },
  mcc: { width: 80, height: 100 },
};

/** Default label background: the Private Cellar warm white (palette "warm-white"). */
export const DEFAULT_BACKGROUND = "#f3efe8";

export type TextAlign = "left" | "center" | "right";
export type TextWeight = "regular" | "bold";

interface BaseElement {
  /** Stable, unique within the document. */
  id: string;
  /** Top-left corner, mm from the label's top-left. */
  x: number;
  y: number;
  /** Bounding box size in mm. */
  width: number;
  height: number;
}

export interface TextElement extends BaseElement {
  type: "text";
  text: string;
  font: FontKey;
  /** CAP HEIGHT in mm (the height of a capital "H"), not the CSS font size. */
  size_mm: number;
  color: string;
  align: TextAlign;
  weight: TextWeight;
  italic: boolean;
  /** Tracking in em (0.1 = 10% of the font size between glyphs). */
  letter_spacing_em: number;
  /** Line advance as a multiple of the font size (CSS line-height). */
  line_height: number;
  // `height` is derived from the wrapped text (see render.ts measureTextHeightMm)
  // and kept in sync by the editor; rendering never clips to it.
}

export interface ImageElement extends BaseElement {
  type: "image";
  /** The mock-up's single attached upload, resolved to an object URL at runtime. */
  src: "uploaded";
  fit: "contain";
  /**
   * v2: caption for an EMPTY image slot (a template's logo/photo position),
   * e.g. "Add your logo". Drawn inside the editor's dashed placeholder and
   * template thumbnails only — never in an export, and never once an image
   * has been uploaded.
   */
  placeholder?: string;
}

export interface RectElement extends BaseElement {
  type: "rect";
  stroke: string;
  /** Border width in mm, drawn INSIDE the bounding box. 0 = no border. */
  stroke_mm: number;
  fill?: string | null;
}

export interface EllipseElement extends BaseElement {
  type: "ellipse";
  stroke: string;
  stroke_mm: number;
  fill?: string | null;
}

/** A horizontal divider. Its box height always equals thickness_mm. */
export interface LineElement extends BaseElement {
  type: "line";
  stroke: string;
  thickness_mm: number;
}

/**
 * v2: a built-in vector ornament (ornaments.ts), recoloured to `color`.
 * The box always keeps the ornament's aspect (width ÷ height of its viewBox);
 * the SVG is drawn contained and centred in it.
 */
export interface OrnamentElement extends BaseElement {
  type: "ornament";
  asset: OrnamentKey;
  color: string;
}

export type LabelElement =
  | TextElement
  | ImageElement
  | RectElement
  | EllipseElement
  | LineElement
  | OrnamentElement;

export type ElementType = LabelElement["type"];

export interface CanvasData {
  version: typeof CANVAS_VERSION;
  bottle: BottleType;
  label: { width_mm: number; height_mm: number };
  background: { color: string };
  elements: LabelElement[];
}

// ---- Limits (shared by the editor, the inspector and the guard) -------------

export const LIMITS = {
  /** Smallest box an element can be resized to, mm. */
  minSizeMm: 3,
  textSizeMm: { min: 1.2, max: 30 },
  strokeMm: { min: 0.3, max: 2 },
  letterSpacingEm: { min: -0.05, max: 0.5 },
  lineHeight: { min: 0.8, max: 2.5 },
  maxElements: 60,
  maxTextLength: 400,
  /** Longest image-slot caption (ImageElement.placeholder). */
  maxPlaceholderLength: 60,
} as const;

// ---- Ids -------------------------------------------------------------------

let idCounter = 0;

/**
 * A short unique element id. Uses crypto.randomUUID when present (browsers,
 * Node 19+), else a time + counter fallback. Only ever called from event
 * handlers — never during render — so it cannot cause hydration mismatches.
 */
export function newId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return "el_" + c.randomUUID().replace(/-/g, "").slice(0, 12);
  idCounter += 1;
  return `el_${Date.now().toString(36)}${idCounter.toString(36)}`;
}

// ---- Construction -----------------------------------------------------------

export function isBottleType(v: unknown): v is BottleType {
  return v === "wine" || v === "mcc";
}

/** An empty warm-white label for the given bottle. */
export function defaultDocument(bottle: BottleType = "wine"): CanvasData {
  const mm = LABEL_MM[bottle];
  return {
    version: CANVAS_VERSION,
    bottle,
    label: { width_mm: mm.width, height_mm: mm.height },
    background: { color: DEFAULT_BACKGROUND },
    elements: [],
  };
}

// ---- Guard ------------------------------------------------------------------

const HEX = /^#[0-9a-f]{6}$/i;
const isHex = (v: unknown): v is string => typeof v === "string" && HEX.test(v);
const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

function isBox(e: Record<string, unknown>): boolean {
  return (
    typeof e.id === "string" &&
    e.id.length > 0 &&
    isNum(e.x) &&
    isNum(e.y) &&
    isNum(e.width) &&
    isNum(e.height) &&
    (e.width as number) >= 0 &&
    (e.height as number) >= 0
  );
}

export function isElement(v: unknown): v is LabelElement {
  if (!isObj(v) || !isBox(v)) return false;
  switch (v.type) {
    case "text":
      return (
        typeof v.text === "string" &&
        typeof v.font === "string" &&
        (FONT_KEYS as readonly string[]).includes(v.font) &&
        isNum(v.size_mm) &&
        (v.size_mm as number) > 0 &&
        isHex(v.color) &&
        (v.align === "left" || v.align === "center" || v.align === "right") &&
        (v.weight === "regular" || v.weight === "bold") &&
        typeof v.italic === "boolean" &&
        isNum(v.letter_spacing_em) &&
        isNum(v.line_height) &&
        (v.line_height as number) > 0
      );
    case "image":
      return (
        v.src === "uploaded" &&
        v.fit === "contain" &&
        (v.placeholder === undefined ||
          (typeof v.placeholder === "string" && v.placeholder.length <= LIMITS.maxPlaceholderLength))
      );
    case "rect":
    case "ellipse":
      return (
        isHex(v.stroke) &&
        isNum(v.stroke_mm) &&
        (v.stroke_mm as number) >= 0 &&
        (v.fill === undefined || v.fill === null || isHex(v.fill))
      );
    case "line":
      return isHex(v.stroke) && isNum(v.thickness_mm) && (v.thickness_mm as number) > 0;
    case "ornament":
      return isOrnamentKey(v.asset) && isHex(v.color);
    default:
      return false;
  }
}

/** Strict structural check of a current-version document. */
export function isCanvasData(v: unknown): v is CanvasData {
  if (!isObj(v)) return false;
  if (v.version !== CANVAS_VERSION) return false;
  if (!isBottleType(v.bottle)) return false;
  if (!isObj(v.label) || !isNum(v.label.width_mm) || !isNum(v.label.height_mm)) return false;
  if (!isObj(v.background) || !isHex(v.background.color)) return false;
  if (!Array.isArray(v.elements) || v.elements.length > LIMITS.maxElements) return false;
  const ids = new Set<string>();
  for (const el of v.elements) {
    if (!isElement(el)) return false;
    if (ids.has(el.id)) return false;
    ids.add(el.id);
  }
  return true;
}

/**
 * Turn whatever came out of the database into a valid current (v2) document.
 * - `{}` / null / garbage / an unknown version → defaultDocument(fallbackBottle).
 * - A v1 document (M4 as first built) → upgraded: re-stamped `version: 2`,
 *   its bottle, background and elements carried over through the same
 *   per-element guard. Nothing else changes — every v1 element is a valid v2
 *   element — so a saved v1 design is never lost.
 * - A v2 document → returned as-is, with the label size re-pinned to
 *   LABEL_MM for its bottle (the label size is not user-editable).
 * - A partially valid document (some bad elements) → the bad elements are
 *   dropped rather than losing the whole design.
 * `fallbackBottle` should be the record's `bottle_type` column.
 */
export function migrate(raw: unknown, fallbackBottle: BottleType = "wine"): CanvasData {
  if (!isObj(raw)) return defaultDocument(fallbackBottle);
  if (typeof raw.version !== "number" || !READABLE_CANVAS_VERSIONS.includes(raw.version)) {
    return defaultDocument(fallbackBottle);
  }
  const bottle = isBottleType(raw.bottle) ? raw.bottle : fallbackBottle;
  const doc = defaultDocument(bottle);
  if (isObj(raw.background) && isHex(raw.background.color)) {
    doc.background.color = raw.background.color;
  }
  if (Array.isArray(raw.elements)) {
    const ids = new Set<string>();
    for (const el of raw.elements) {
      if (doc.elements.length >= LIMITS.maxElements) break;
      if (!isElement(el) || ids.has(el.id)) continue;
      ids.add(el.id);
      doc.elements.push(el);
    }
  }
  return doc;
}

// ---- Small immutable helpers ------------------------------------------------

export function findElement(doc: CanvasData, id: string | null): LabelElement | null {
  if (!id) return null;
  return doc.elements.find((e) => e.id === id) ?? null;
}

export function replaceElement(doc: CanvasData, el: LabelElement): CanvasData {
  return { ...doc, elements: doc.elements.map((e) => (e.id === el.id ? el : e)) };
}

export function removeElement(doc: CanvasData, id: string): CanvasData {
  return { ...doc, elements: doc.elements.filter((e) => e.id !== id) };
}

export function imageElement(doc: CanvasData): ImageElement | null {
  return (doc.elements.find((e) => e.type === "image") as ImageElement | undefined) ?? null;
}
